import React, { useRef } from 'react'
import DatePicker from 'material-ui/DatePicker'
import styles from '../../styles'
import { COUNTRY_CODES } from '../../../../../../../../shared/constants'
import { validateBreakTime, validateBreakTimeOverlap } from '../../utils'
import TimeRanges from '../../parts/TimeRanges'

const EventSchedule = ({
  eventSchedule,
  handleEventScheduleChange,
  visible,
  city,
  validationErrors,
  handleValidationError,
}) => {
  const startDatePickerRef = useRef(null)
  const endDatePickerRef = useRef(null)

  if (!visible) return null
  const countryCode = city.country ? city.country.code : null

  const onBreakTimeStartChange = (id, time) => {
    const newBreakTimes = eventSchedule.breakTimes.map(breakTime =>
      breakTime.id === id ? { ...breakTime, start: time } : breakTime,
    )
    handleEventScheduleChange('breakTimes', newBreakTimes)
    validateBreakTime(
      id,
      time,
      eventSchedule.breakTimes.find(bt => bt.id === id).end,
      'start',
      handleValidationError,
      newBreakTimes,
      'breakTimes',
    )
  }

  const onBreakTimeEndChange = (id, time) => {
    const newBreakTimes = eventSchedule.breakTimes.map(breakTime =>
      breakTime.id === id ? { ...breakTime, end: time } : breakTime,
    )
    handleEventScheduleChange('breakTimes', newBreakTimes)
    validateBreakTime(
      id,
      eventSchedule.breakTimes.find(bt => bt.id === id).start,
      time,
      'end',
      handleValidationError,
      newBreakTimes,
      'breakTimes',
    )
  }

  const handleAddBreakTime = () => {
    const newBreakTimes = [
      ...eventSchedule.breakTimes,
      {
        id: Math.max(...eventSchedule.breakTimes.map(bt => bt.id), 0) + 1,
        start: null,
        end: null,
      },
    ]
    handleEventScheduleChange('breakTimes', newBreakTimes)
  }

  const handleRemoveBreakTime = id => {
    const newBreakTimes = eventSchedule.breakTimes.filter(
      breakTime => breakTime.id !== id,
    )
    handleEventScheduleChange('breakTimes', newBreakTimes)
    handleValidationError(`breakTimes-${id}-start`, null)
    handleValidationError(`breakTimes-${id}-end`, null)
    validateBreakTimeOverlap(newBreakTimes, handleValidationError)
  }

  const handleStartDateChange = date => {
    handleEventScheduleChange('startDate', date)
    handleValidationError('startDate', null)
  }

  const handleEndDateChange = date => {
    handleEventScheduleChange('endDate', date)
    handleValidationError('endDate', null)
  }

  return (
    <div style={styles.contentArea}>
      <div style={styles.headerSchedulingContainer}>
        <div style={{ ...styles.contentTitle, cursor: 'pointer' }}>
          Scheduling
        </div>
        <div style={{ ...pageStyles.subtitle, alignSelf: 'flex-start' }}>
          Define when this event is operational for AI Roster scheduling
        </div>
      </div>
      <div>
        <div style={pageStyles.inputsContainer}>
          <div style={styles.datePickerContainer}>
            <div style={styles.inputTitle}>Starting Date</div>
            <DatePicker
              ref={startDatePickerRef}
              hintText={
                countryCode == COUNTRY_CODES.UNITED_STATES
                  ? 'mm/dd/yyyy'
                  : 'dd/mm/yyyy'
              }
              hintStyle={{
                position: 'absolute',
                top: 2,
              }}
              value={eventSchedule.startDate || null}
              onChange={(event, date) => {
                handleStartDateChange(date)
              }}
              formatDate={date => {
                if (!date) return ''
                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')
                return countryCode == COUNTRY_CODES.UNITED_STATES
                  ? `${month}/${day}/${year}`
                  : `${day}/${month}/${year}`
              }}
              shouldDisableDate={date => {
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                if (date < today) return true
                return false
              }}
              minDate={new Date()}
              maxDate={eventSchedule.endDate || null}
              textFieldStyle={pageStyles.datePickerInput(
                validationErrors.startDate,
              )}
              underlineShow={false}
            />
            {validationErrors.startDate && (
              <div style={pageStyles.errorMessage}>
                {validationErrors.startDate}
              </div>
            )}
          </div>

          <div style={styles.datePickerContainer}>
            <div style={styles.inputTitle}>Ending Date</div>
            <DatePicker
              ref={endDatePickerRef}
              hintText={
                countryCode == COUNTRY_CODES.UNITED_STATES
                  ? 'mm/dd/yyyy'
                  : 'dd/mm/yyyy'
              }
              hintStyle={{
                position: 'absolute',
                top: 2,
              }}
              value={eventSchedule.endDate || null}
              onChange={(event, date) => {
                handleEndDateChange(date)
              }}
              formatDate={date => {
                if (!date) return ''
                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')
                return countryCode == COUNTRY_CODES.UNITED_STATES
                  ? `${month}/${day}/${year}`
                  : `${day}/${month}/${year}`
              }}
              shouldDisableDate={date => {
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                if (date < today) return true
                return false
              }}
              minDate={eventSchedule.startDate || new Date()}
              textFieldStyle={pageStyles.datePickerInput(
                validationErrors.endDate,
              )}
              underlineShow={false}
            />
            {validationErrors.endDate && (
              <div style={pageStyles.errorMessage}>
                {validationErrors.endDate}
              </div>
            )}
          </div>
        </div>
      </div>
      <div style={pageStyles.breakTimesContainer}>
        <TimeRanges
          timeRanges={eventSchedule.breakTimes}
          onStartChange={onBreakTimeStartChange}
          onEndChange={onBreakTimeEndChange}
          onRemove={handleRemoveBreakTime}
          validationErrors={validationErrors}
          handleAddTime={handleAddBreakTime}
          addButtonTooltip={{
            description: "You can add 'break' times for the location 3 times.",
          }}
        />
      </div>
    </div>
  )
}

export default EventSchedule

const pageStyles = {
  subtitle: {
    color: '#707087',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '18px',
  },
  inputsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  datePickerInput: error => ({
    backgroundColor: 'white',
    width: '100%',
    height: 32,
    paddingLeft: 16,
    paddingRight: 40,
    borderRadius: 8,
    border: '1px solid #E2E2FC',
    borderColor: error ? '#FF0000' : '#E2E2FC',
  }),
  datePickerContainer: {
    width: '48%',
  },
  breakTimesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    alignItems: 'center',
  },
  errorMessage: {
    color: 'red',
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '18px',
  },
}
